import { faLightbulb } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { If, RightDrawer, UnborderCollapse } from 'aa_common/front-end/components'
import { someTrue } from 'aa_common/front-end/helpers'
import { useBoolean } from 'aa_common/front-end/hooks'
import { getNs } from 'common/i18n-config'
import { Asset, FORM_MODES } from 'common/models'
import { PresenterPreFillDraftAssetResponse } from 'common/open-api'
import { HorizontalLine } from 'components/atoms'
import { useFormikContext } from 'formik'
import get from 'lodash/get'
import React from 'react'

import { AcquisitionCostSection } from './components/AcquisitionCostSection'
import { AfterAcquisitionSection } from './components/AfterAcquisitionSection'
import { AIDisclaimer } from './components/ai-disclaimer'
import { AIFormContent } from './components/ai-form-content'
import { AILoading } from './components/ai-loading'
import { AINoResult } from './components/ai-no-result'
import { AISubmittedResult } from './components/ai-submitted-result'
import { BeforeAcquisitionSection } from './components/BeforeAcquisitionSection'
import { DepreciationLedgers } from './components/DepreciationLedgers'
import { OtherArea } from './components/OtherArea'
import { RightArea } from './components/RightArea'
import { SubmitArea } from './components/submit-area'
import { UploadArea } from './components/UploadArea'
import { useAcquisitionCostReference } from './hooks/use-acquisition-cost-reference-new'
import { useAIAssistantDrawer } from './hooks/use-ai-assistant-drawer'
import { useAISuggestionHandlers } from './hooks/use-ai-suggestion-handlers'
import { useBookValueReference } from './hooks/use-book-value-reference'
import { useExciseData } from './hooks/use-excise-data'
import { useInitAcquisitionCostInput } from './hooks/use-init-acquisition-cost-input'
import { useInitialFormData } from './hooks/use-initial-form-data'
import { useSaveTempData } from './hooks/use-save-temp-data'
import { useSelectAssetCategory } from './hooks/use-select-asset-category'
import { useSelectedAssetCategoryChange } from './hooks/use-selected-asset-category-change'
import { useSetFromCalculatedDraft } from './hooks/use-set-from-calculated-draft'
import { useFormValidation, useSubmitOnError } from './hooks/useFormValidation'
import { AIDisclaimerWrapper } from './styles'

const { t, useNsReady } = getNs(['components/organisms/assets/asset-form', 'common'])

const { LeftGroup } = UnborderCollapse

export const FormData: React.FC<Props> = ({
  initialValues,
  formMode,
  isTemporaryData,
  onSaveTemp,
  onHandleReloadMasterOptions,
  showRequiredError,
  onSubmit,
  confirming,
  isDraftFA,
  isReadOnlyDraftFA,
  calculatedDraftFA,
  shouldDisableSubmit,
}) => {
  useNsReady()
  const { values } = useFormikContext()

  const reductionAmount = get(values, 'reduction_amount')
  const netAcquisitionCost = get(values, 'net_acquisition_cost')
  const assetName = get(values, 'name')
  const photos = get(values, 'photos')

  const { setExciseSettingDate } = useExciseData(formMode, isDraftFA)
  const [bookValueReferUpdatedManually, setBookValueReferUpdatedManually] = useBoolean(false)
  const acquisitionCostReference = useAcquisitionCostReference()
  const bookValueReference = useBookValueReference(acquisitionCostReference, netAcquisitionCost)
  const { resultSelectedAssetCategory, assetType } = useSelectAssetCategory()

  useFormValidation()
  useSubmitOnError(onSubmit)
  useSaveTempData(formMode, onSaveTemp)

  useInitialFormData(initialValues, isTemporaryData, setExciseSettingDate)
  useInitAcquisitionCostInput(initialValues)
  useSetFromCalculatedDraft(calculatedDraftFA)

  const {
    aiState,
    setAIState,
    suggestions,
    onSubmit: onSubmitAIForm,
    isLoading,
    hasSuggestionResult,
    hasSubmitted,
    onTryAgain,
    onReflect,
    reflectedAssetCategoryIdRef,
    showRunAIAnalysisButton,
    isRegisteredCategory,
  } = useAISuggestionHandlers(assetName, photos?.[0])
  useSelectedAssetCategoryChange(formMode, resultSelectedAssetCategory.data, reflectedAssetCategoryIdRef)
  const { visible, closeDrawer, openDrawer } = useAIAssistantDrawer(onTryAgain)

  return (
    <>
      <UnborderCollapse title={t('title_basic_info')} collapsable={false}>
        <LeftGroup>
          <BeforeAcquisitionSection
            formMode={formMode}
            loadingAssetCategory={resultSelectedAssetCategory.isLoading}
            showRequiredError={showRequiredError}
            setBookValueReferUpdatedManually={setBookValueReferUpdatedManually}
            setExciseSettingDate={setExciseSettingDate}
            onShowConfirm={openDrawer}
            isReadOnlyDraftFA={isReadOnlyDraftFA}
          />
          <AcquisitionCostSection
            formMode={formMode}
            loadingAssetCategory={resultSelectedAssetCategory.isLoading}
            showRequiredError={showRequiredError}
            setBookValueReferUpdatedManually={setBookValueReferUpdatedManually}
            acquisitionCostValueRefer={acquisitionCostReference}
            isDraftFA={isDraftFA}
          />
          <AfterAcquisitionSection
            formMode={formMode}
            showRequiredError={showRequiredError}
            isReadOnlyDraftFA={isReadOnlyDraftFA}
          />
        </LeftGroup>
        <RightArea onHandleReloadMasterOptions={onHandleReloadMasterOptions} isReadOnlyDraftFA={isReadOnlyDraftFA} />
      </UnborderCollapse>
      <HorizontalLine />
      <UploadArea isReadOnlyDraftFA={isReadOnlyDraftFA} />
      <HorizontalLine />
      <DepreciationLedgers
        initialValues={initialValues}
        formMode={formMode}
        assetType={assetType}
        bookValueReference={bookValueReference}
        bookValueReferUpdatedManually={bookValueReferUpdatedManually}
        showRequiredError={showRequiredError}
        setBookValueReferUpdatedManually={setBookValueReferUpdatedManually}
        setExciseSettingDate={setExciseSettingDate}
        acquisitionCostValueRefer={acquisitionCostReference}
        isDraftFA={isDraftFA}
        isReadOnlyDraftFA={isReadOnlyDraftFA}
      />
      <HorizontalLine />
      <OtherArea isReadOnlyDraftFA={isReadOnlyDraftFA} />
      <SubmitArea
        isDraftFA={isDraftFA}
        formMode={formMode}
        confirming={confirming}
        disable={someTrue(
          Boolean(acquisitionCostReference && reductionAmount > acquisitionCostReference),
          !!shouldDisableSubmit
        )}
      />

      <RightDrawer width={479} visible={visible} onClose={closeDrawer} title={t('ai_suggestion')}>
        <span>
          <FontAwesomeIcon icon={faLightbulb} style={{ fontSize: 18, marginRight: 4, color: '#3b7de9' }} />
          {t('ai_drawer_description')}
        </span>
        <If condition={isLoading}>
          <AILoading />
        </If>
        <If condition={!hasSuggestionResult && !isLoading && !hasSubmitted}>
          <AIFormContent
            aiState={aiState}
            setAIState={setAIState}
            onSubmit={onSubmitAIForm}
            showRunAIAnalysisButton={showRunAIAnalysisButton}
          />
        </If>

        <If condition={hasSuggestionResult}>
          <AISubmittedResult
            imageSrc={aiState?.image?.url}
            suggestions={suggestions}
            onTryAgain={onTryAgain}
            onReflect={onReflect}
            isRegisteredCategory={isRegisteredCategory}
          />
        </If>
        <If condition={!hasSuggestionResult && hasSubmitted}>
          <AINoResult onTryAgain={onTryAgain} />
        </If>

        <If condition={!hasSuggestionResult}>
          <AIDisclaimerWrapper>
            <AIDisclaimer />
          </AIDisclaimerWrapper>
        </If>
      </RightDrawer>
    </>
  )
}

type Props = {
  initialValues: Asset
  formMode: FORM_MODES
  isTemporaryData: boolean
  onSaveTemp?: (dataType: string, data: any, extraKey?: string) => void
  onHandleReloadMasterOptions?: (pageName: string) => void
  showRequiredError?: boolean
  onSubmit?: (values: any) => void
  confirming?: boolean
  isDraftFA?: boolean
  isReadOnlyDraftFA?: boolean
  calculatedDraftFA?: PresenterPreFillDraftAssetResponse
  shouldDisableSubmit?: boolean
}
